import { DatasetElementType } from '@mui/x-charts/internals';
import { useDuckQuery } from '../../../../../duckDB/hooks/useDuckQuery';
import { formatStringList } from '../../../../../duckDB/utils/formatStringList';
import { formattedDateColumn } from '../../../../../duckDB/utils/formattedDateColumn';
import {
  CountMode,
  StageReportData,
} from '../../../../components/ReportBarChart/types';

export const useStageReportChartData = (
  scanDate: string,
  ranchIds: string[],
  isTableLoading: boolean,
  countMode: CountMode = 'total'
): {
  data: DatasetElementType<string | number>[];
  yAxisValueFormatter: (value: string) => string;
  loading: boolean;
} => {
  const { data: chartData, isLoading } = useDuckQuery<StageReportData>(
    `
    SELECT 
    CONCAT(block_name, '|', block_id) as dataKey,
    ranch_id as ranchId, 
    block_id as blockId, 
    block_name as blockName, 
    scan_id as scanId,
    ${formattedDateColumn('scan_date')} AS scanDate, 
    array_agg({'stageId': stage_id, 
    'count': stage_count,
    'countPerPlant': count_per_plant}) AS stageCounts,
    sum(CAST(stage_count AS FLOAT)) AS totalCount,
    sum(CAST(count_per_plant AS FLOAT)) AS totalCountPerPlant,
    FROM stage_report_table WHERE scan_date='${scanDate}' 
    AND ranch_id IN ${formatStringList(ranchIds)} 
    GROUP BY ALL
    ORDER BY block_name ASC
    `,
    isTableLoading || !ranchIds || !scanDate
  );

  return {
    ...formatStageReportBarChartData(chartData, countMode),
    loading: isTableLoading || isLoading,
  };
};

const formatStageReportBarChartData = (
  data: StageReportData[] | null,
  countMode: CountMode = 'total'
) => {
  if (!data) {
    return { data: [], yAxisValueFormatter: () => '' };
  }
  const formattedData = data.map((item) =>
    Object.assign(
      {
        ...item,
        totalCount:
          countMode === 'total' ? item.totalCount : item.totalCountPerPlant,
      },
      ...Array.from(item.stageCounts).map((stageCount) => ({
        [stageCount.stageId]:
          countMode === 'total'
            ? Math.round(stageCount.count) || 0
            : Math.round(stageCount.countPerPlant) || 0,
      }))
    )
  );

  return {
    data: formattedData,
    yAxisValueFormatter: (value: string) => value.split('|', 1).at(0) || '',
  };
};
